/**
 * Estados de un lote y su lectura en pantalla.
 *
 * Son los cuatro de `services/api/schemas/catalog.py::BatchStatus`:
 *
 *   open      todavía se está envasando
 *   released  en el mercado
 *   recalled  retirado — alerta sanitaria al consumidor, regla crítica
 *   closed    agotado
 *
 * `recalled` es el único en rojo, y es el único que tiene que estarlo: en este
 * dashboard el color significa "alguien tiene que actuar".
 */
import type { MessageKey } from '@/i18n/messages';
import type { Tone } from '@/ui/Badge';

export const BATCH_STATUSES = ['open', 'released', 'recalled', 'closed'] as const;

export type BatchStatus = (typeof BATCH_STATUSES)[number];

export function isBatchStatus(value: string): value is BatchStatus {
  return (BATCH_STATUSES as readonly string[]).includes(value);
}

export const BATCH_STATUS_LABEL_KEYS: Record<BatchStatus, MessageKey> = {
  open: 'batches.status.open',
  released: 'batches.status.released',
  recalled: 'batches.status.recalled',
  closed: 'batches.status.closed',
};

export const BATCH_STATUS_TONES: Record<BatchStatus, Tone> = {
  open: 'neutral',
  released: 'verified',
  recalled: 'critical',
  closed: 'neutral',
};

/**
 * Estados que se pueden elegir en un formulario de edición.
 *
 * `recalled` NO está: el retiro tiene su propio endpoint porque fija
 * `recalled_at`, exige un motivo y es lo que dispara la alerta sanitaria al
 * consumidor. La API rechaza un `PATCH` con `status: recalled`
 * (`use_recall_endpoint`); acá directamente no se ofrece, para que nadie
 * descubra por un 400 que retirar un lote no es cambiar un desplegable.
 */
export const EDITABLE_BATCH_STATUSES = BATCH_STATUSES.filter(
  (status): status is Exclude<BatchStatus, 'recalled'> => status !== 'recalled',
);
